// src/pages/Terms.js
import React from 'react';
import { Helmet } from 'react-helmet-async';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Meteors } from '../components/ui/meteors';

import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

export default function Terms() {
  const { darkMode, toggleDarkMode } = useTheme();
  const { 
    user, 
    userLoading, 
    isAuthLoading, 
    handleLogout, 
    openLogin, 
    openRegister 
  } = useAuth();

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Meteors Background */}
      <div className="absolute inset-0 z-[-1] pointer-events-none overflow-hidden">
        <Meteors count={35} angle={220} />
      </div>

      {/* SEO */}
      <Helmet>
        <title>Terms of Service - SErraND Kenya</title>
        <meta
          name="description"
          content="Review SErraND's Terms of Service, outlining the rules and guidelines for using our platform to connect with service providers in Kenya."
        />
        <meta
          name="keywords"
          content="SErraND, terms of service, Kenya, service providers, platform guidelines, user agreement"
        />
        <meta name="author" content="Marvin Useki" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.serrand.org/terms/" />
        <meta property="og:title" content="Terms of Service - SErraND Kenya" />
        <meta
          property="og:description"
          content="Understand the Terms of Service for using SErraND, your trusted platform for service providers in Kenya."
        />
        <meta property="og:url" content="https://www.serrand.org/terms/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/terms-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service - SErraND Kenya" />
        <meta name="twitter:description" content="SErraND's Terms of Service for users in Kenya." />
        <meta name="twitter:image" content="https://www.serrand.org/images/terms-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'SErraND Terms of Service',
            url: 'https://www.serrand.org/terms/',
            description:
              'Read the Terms of Service for SErraND, governing the use of our platform connecting clients with trusted service providers in Kenya.',
            publisher: {
              '@type': 'Organization',
              name: 'SErraND',
              url: 'https://www.serrand.org',
              logo: 'https://www.serrand.org/images/logo.png',
            },
          })}
        </script>
      </Helmet>

      {/* Navbar */}
      <Navbar
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={openLogin}
        openRegister={openRegister}
      />

      {/* Terms Content */}
      <section className="relative py-16 px-4 max-w-5xl mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            SErraND Terms of Service
          </h1>
          <p className="text-muted-foreground text-lg">
            Last Updated: May 31, 2025
          </p>
        </div>

        <Card className="border-border">
          <CardContent className="p-8 md:p-12 prose prose-neutral dark:prose-invert max-w-none text-foreground/90 leading-relaxed">
            
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p>
                Welcome to SErraND, a vibrant platform designed to connect clients with trusted service providers across Kenya. By accessing or using SErraND.org, whether as a client seeking services or a service provider offering your expertise, you agree to be bound by these Terms of Service (“ToS”). These terms govern your use of our platform, including all content, functionality, and services offered. If you do not agree with these terms, please do not access or use our platform.
              </p>
              <p className="mt-4">
                SErraND is committed to fostering a safe, transparent, and reliable community. These terms outline your rights and responsibilities to ensure a positive experience for all users.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. User-Generated Content and Acceptable Use</h2>
              <p>
                SErraND allows users to create and share content, such as service listings, profiles, and reviews, to promote their services or engage with the community. All content must adhere to the following guidelines:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Be lawful, respectful, and appropriate.</li>
                <li>Not contain illegal, defamatory, harassing, hateful, or obscene material.</li>
                <li>Not infringe on intellectual property rights, including copyrights or trademarks.</li>
                <li>Not misrepresent your identity, qualifications, or services.</li>
              </ul>
              <p className="mt-4">
                The use of SErraND’s name, logo, or branding in a misleading or unauthorized manner is strictly prohibited. We reserve the right to remove any content that violates these guidelines and may suspend or terminate accounts that repeatedly violate our policies.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. Pricing and Promotions</h2>
              <p>
                SErraND is free to use for both clients and service providers. However, we charge a 10% commission on all transactions facilitated through the platform. This commission supports the maintenance and improvement of our services and may be adjusted at SErraND’s discretion with prior notice.
              </p>
              <p className="mt-4">
                Service providers may opt for promoted placements to enhance their visibility on the platform. These promotions are optional, and participation does not influence SErraND’s impartiality or guarantee service quality. Details about promotion costs and benefits are available in your provider dashboard.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Prohibited Conduct</h2>
              <p>
                To maintain a safe and respectful community, users are prohibited from:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Engaging in spamming, phishing, or unsolicited communications.</li>
                <li>Harassing, threatening, or discriminating against other users.</li>
                <li>Conducting illegal activities, including fraud or misrepresentation.</li>
                <li>Attempting to hack, disrupt, or bypass SErraND’s security measures.</li>
              </ul>
              <p className="mt-4">
                SErraND will cooperate fully with Kenyan law enforcement authorities in response to illegal activities. Violations may result in account suspension, termination, or legal action.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Intellectual Property</h2>
              <p>
                SErraND owns all rights to its branding, including the “SErraND” name, logo, domain (SErraND.org), and related assets. Users may not use, reproduce, or distribute SErraND’s intellectual property without prior written consent. User-generated content remains the property of the user, but by posting it, you grant SErraND a non-exclusive, royalty-free license to use, display, and distribute it on the platform.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Disclaimer of Employment</h2>
              <p>
                SErraND is a facilitator, not an employer of service providers. We connect clients with independent providers and do not control, supervise, or guarantee the quality, safety, or legality of their services. Clients are responsible for conducting due diligence before engaging a provider. SErraND is not liable for any issues arising from services provided.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Dispute Resolution Between Users</h2>
              <p>
                SErraND encourages users to resolve disputes directly. For remote services with verifiable proof, SErraND may offer mediation on a best-effort basis. The dispute resolution process is as follows:
              </p>
              <ol className="list-decimal pl-6 mt-4 space-y-2">
                <li><strong>Mediation</strong>: SErraND facilitates communication to reach a mutual resolution.</li>
                <li><strong>Arbitration</strong>: If mediation fails, disputes may proceed to arbitration under Kenyan law and the rules of a recognized arbitral institution.</li>
                <li><strong>Court Action</strong>: If arbitration is impractical, disputes may be escalated to Kenyan courts.</li>
              </ol>
              <p className="mt-4">
                Tort claims or allegations of damage must be addressed between the parties involved. Serious matters may be referred to Kenyan authorities.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Modification of Terms</h2>
              <p>
                SErraND may update these ToS at any time to reflect changes in our practices or legal requirements. We will notify users of material changes via website notices and/or email. Continued use of the platform after changes constitutes acceptance of the updated terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Force Majeure</h2>
              <p>
                SErraND is not liable for delays or failures in performance due to events beyond our control, such as natural disasters, war, terrorism, riots, strikes, or shortages of resources.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless SErraND, its officers, directors, employees, and agents from any claims, damages, or liabilities arising from your use of the platform, violation of these ToS, or infringement of any third-party rights.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">11. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by Kenyan law, SErraND is not liable for indirect, incidental, special, or consequential damages, including loss of profits, data, or goodwill, arising from your use of the platform.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">12. Disclaimer of Warranty</h2>
              <p>
                SErraND provides the platform “as is” without warranties of any kind. We do not guarantee uninterrupted, secure, or error-free service, nor do we warrant the accuracy or reliability of results obtained from the platform.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">13. Consent to Electronic Communications</h2>
              <p>
                By using SErraND, you consent to receive electronic communications, including account notices, updates, and promotional messages via email or SMS. You may opt out of non-essential communications through your account settings.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">14. Relationship of the Parties</h2>
              <p>
                No partnership, joint venture, or employment relationship is created between SErraND and users. Users have no authority to bind SErraND in any way.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">15. Entire Agreement</h2>
              <p>
                These ToS, along with the Privacy Policy, constitute the entire agreement between you and SErraND, superseding all prior agreements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">16. Feedback Mechanism</h2>
              <p>
                We welcome your feedback to improve SErraND. Submit suggestions via our website’s feedback form or email{' '}
                <a href="mailto:serrand.support@serrand.org" className="text-primary hover:underline">
                  serrand.support@serrand.org
                </a>
                . By submitting feedback, you grant SErraND the right to use and adapt it without restriction.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">17. Governing Law and Jurisdiction</h2>
              <p>
                These ToS are governed by the laws of Kenya. Any disputes shall be resolved exclusively in the courts of Kenya.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">18. Contact Information</h2>
              <p>
                For questions about these ToS, contact us at{' '}
                <a href="mailto:serrand.support@serrand.org" className="text-primary hover:underline">
                  serrand.support@serrand.org
                </a>.
              </p>
            </section>

          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
